/**
 * FNV-1a state checksum — the tool that proves determinism (SPEC §4.6).
 *
 * Every simulated system contributes its state to a checksum each tick. Two runs from the same
 * seed must produce an identical checksum sequence; the first tick where they differ localises
 * the non-determinism precisely, which is the difference between a five-minute fix and a day.
 *
 * Floats are hashed by their IEEE-754 bits, not by value, so `-0` and `0` are distinguished and
 * no rounding hides a divergence.
 */

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

// Module-scope scratch: a checksum is taken every tick, so this must not allocate.
const scratchBuffer = new ArrayBuffer(8);
const scratchF64 = new Float64Array(scratchBuffer);
const scratchU32 = new Uint32Array(scratchBuffer);

export class Checksum {
  private hash = FNV_OFFSET_BASIS;

  /** Start a fresh checksum. */
  reset(): void {
    this.hash = FNV_OFFSET_BASIS;
  }

  /** Mix in a 32-bit integer. */
  addInt(value: number): void {
    let h = this.hash;
    const v = value | 0;
    h = Math.imul(h ^ (v & 0xff), FNV_PRIME);
    h = Math.imul(h ^ ((v >>> 8) & 0xff), FNV_PRIME);
    h = Math.imul(h ^ ((v >>> 16) & 0xff), FNV_PRIME);
    h = Math.imul(h ^ ((v >>> 24) & 0xff), FNV_PRIME);
    this.hash = h;
  }

  /** Mix in a float by its raw 64-bit representation. Units irrelevant — bits only. */
  addFloat(value: number): void {
    scratchF64[0] = value;
    this.addInt(scratchU32[0]);
    this.addInt(scratchU32[1]);
  }

  /** Current checksum as an unsigned 32-bit integer. */
  get value(): number {
    return this.hash >>> 0;
  }

  /** Current checksum as `0xXXXXXXXX`, for logging. */
  toHex(): string {
    return `0x${(this.hash >>> 0).toString(16).toUpperCase().padStart(8, '0')}`;
  }
}

/** Anything whose state participates in the determinism checksum. */
export interface Checksummable {
  /** Mix this object's full simulation state into the checksum. */
  checksum(into: Checksum): void;
}
